/**
 * ConfirmModal.js
 * A reusable, promise-based confirmation modal with premium styling.
 * 
 * Usage:
 * const isConfirmed = await ConfirmModal.show({
 *     title: 'Are you sure?',
 *     message: 'This action cannot be undone.',
 *     confirmText: 'Delete',
 *     cancelText: 'Cancel',
 *     type: 'danger' // 'danger' | 'info' | 'success'
 * });
 * 
 * if (isConfirmed) { ... }
 */

export const ConfirmModal = {
    show: ({ title = 'Confirm', message = 'Are you sure?', confirmText = 'Confirm', cancelText = 'Cancel', type = 'info' } = {}) => {
        return new Promise((resolve) => {
            // 1. Create Overlay
            const overlay = document.createElement('div');
            overlay.className = 'fixed inset-0 bg-black/60 z-[200] flex items-center justify-center opacity-0 transition-opacity duration-300 backdrop-blur-sm';

            // 2. Determine Styling based on type
            let icon = 'info';
            let iconColor = 'text-blue-500 bg-blue-50 dark:bg-blue-900/30';
            let btnClass = 'bg-blue-600 hover:bg-blue-700';

            if (type === 'danger') {
                icon = 'warning';
                iconColor = 'text-red-500 bg-red-50 dark:bg-red-900/30';
                btnClass = 'bg-red-600 hover:bg-red-700';
            } else if (type === 'success') {
                icon = 'check_circle';
                iconColor = 'text-green-500 bg-green-50 dark:bg-green-900/30';
                btnClass = 'bg-green-600 hover:bg-green-700';
            }

            // 3. Modal HTML
            overlay.innerHTML = `
                <div class="bg-white dark:bg-zinc-900 w-full max-w-sm rounded-2xl shadow-2xl transform scale-95 transition-all duration-300 p-6 flex flex-col items-center text-center relative overflow-hidden">
                    <!-- Decorative Background Blob -->
                    <div class="absolute -top-10 -left-10 w-32 h-32 bg-current opacity-5 rounded-full blur-2xl pointer-events-none ${type === 'danger' ? 'text-red-500' : 'text-blue-500'}"></div>

                    <!-- Icon -->
                    <div class="w-16 h-16 rounded-full ${iconColor} flex items-center justify-center mb-4 shadow-inner">
                        <span class="material-symbols-outlined text-3xl">${icon}</span>
                    </div>

                    <!-- Title & Message -->
                    <h3 class="text-xl font-bold text-zinc-900 dark:text-zinc-100 mb-2">${title}</h3>
                    <p class="text-sm text-zinc-500 dark:text-zinc-400 mb-6 leading-relaxed">
                        ${message.replace(/\n/g, '<br>')}
                    </p>

                    <!-- Buttons -->
                    <div class="flex gap-3 w-full">
                        <button id="btn-cancel" class="flex-1 py-3 bg-gray-100 hover:bg-gray-200 dark:bg-zinc-800 dark:hover:bg-zinc-700 text-gray-700 dark:text-gray-300 rounded-xl font-medium transition-colors text-sm">
                            ${cancelText}
                        </button>
                        <button id="btn-confirm" class="flex-1 py-3 text-white rounded-xl font-bold transition-transform active:scale-95 shadow-lg shadow-${type === 'danger' ? 'red' : 'blue'}-500/30 text-sm ${btnClass}">
                            ${confirmText}
                        </button>
                    </div>
                </div>
            `;

            document.body.appendChild(overlay);

            // Animate In
            requestAnimationFrame(() => {
                overlay.classList.remove('opacity-0');
                const modal = overlay.querySelector('div');
                modal.classList.remove('scale-95');
                modal.classList.add('scale-100');
            });

            const close = (result) => {
                const modal = overlay.querySelector('div');
                overlay.classList.add('opacity-0');
                modal.classList.remove('scale-100');
                modal.classList.add('scale-95');

                setTimeout(() => {
                    resolve(result);
                    overlay.remove();
                }, 300);
            };

            // Bind Events
            overlay.querySelector('#btn-cancel').onclick = () => close(false);
            overlay.querySelector('#btn-confirm').onclick = () => close(true);

            // Click outside to cancel
            overlay.onclick = (e) => {
                if (e.target === overlay) close(false);
            };
        });
    }
};
